import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  POWERJOBS_USER_ID,
  listSupportConversations,
  fetchSupportMessages,
  sendSupportMessage,
  markSupportRead,
  subscribeSupportInbox,
  subscribeSupportThread,
  type SupportConversation,
  type SupportMessage,
} from '../lib/supportService';
import { logger } from '../lib/logger';

function formatListTime(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

function formatMessageTime(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} · ${d.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

const roleLabels: Record<string, string> = {
  candidate: 'Candidat',
  employer: 'Recruteur',
  visitor: 'Visiteur',
};

const roleClasses: Record<string, string> = {
  candidate: 'bg-blue-50 text-blue-700 border-blue-200',
  employer: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  visitor: 'bg-gray-50 text-gray-600 border-gray-200',
};

function Avatar({ name, uri, size = 'md' }: { name: string; uri: string | null; size?: 'md' | 'lg' }) {
  const cls = size === 'lg' ? 'w-11 h-11 text-base' : 'w-10 h-10 text-sm';
  if (uri) {
    return <img src={uri} alt="" className={`${cls} rounded-full object-cover bg-gray-100 flex-shrink-0`} />;
  }
  return (
    <div className={`${cls} rounded-full bg-[#00244F]/10 text-[#00244F] font-bold flex items-center justify-center flex-shrink-0`}>
      {(name || '?').charAt(0).toUpperCase()}
    </div>
  );
}

const Support: React.FC = () => {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState<SupportConversation[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const activeIdRef = useRef<string | null>(null);
  activeIdRef.current = activeId;

  const refreshList = useCallback(async () => {
    try {
      const rows = await listSupportConversations('');
      setConversations(rows);
      setListError(null);
    } catch (err) {
      logger.error('load_support_conversations', 'Support', { error: String(err) });
      setListError('Impossible de charger les conversations support.');
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    refreshList();
    const unsub = subscribeSupportInbox(() => {
      refreshList();
    });
    return unsub;
  }, [refreshList]);

  useEffect(() => {
    if (!activeId) {
      setMessages([]);
      return;
    }
    let cancelled = false;
    setLoadingThread(true);
    fetchSupportMessages(activeId)
      .then((rows) => {
        if (cancelled) return;
        setMessages(rows);
      })
      .catch((err) => logger.error('load_support_thread', 'Support', { error: String(err) }))
      .finally(() => {
        if (!cancelled) setLoadingThread(false);
      });
    markSupportRead(activeId).then(() => refreshList());

    const unsub = subscribeSupportThread(activeId, (message) => {
      setMessages((prev) => (prev.some((m) => m.id === message.id) ? prev : [...prev, message]));
      if (message.senderId !== POWERJOBS_USER_ID && activeIdRef.current === activeId) {
        markSupportRead(activeId).then(() => refreshList());
      }
    });
    return () => {
      cancelled = true;
      unsub();
    };
  }, [activeId, refreshList]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [messages, activeId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter(
      (c) =>
        c.other.displayName.toLowerCase().includes(q) ||
        (c.otherEmail || '').toLowerCase().includes(q) ||
        (c.lastMessagePreview || '').toLowerCase().includes(q),
    );
  }, [conversations, search]);

  const active = useMemo(() => conversations.find((c) => c.id === activeId) || null, [conversations, activeId]);
  const unreadTotal = useMemo(() => conversations.reduce((acc, c) => acc + c.unreadCount, 0), [conversations]);

  const handleSend = async () => {
    if (!active || !draft.trim() || sending) return;
    setSending(true);
    setSendError(null);
    try {
      const message = await sendSupportMessage(active.other.userId, draft.trim());
      setMessages((prev) => (prev.some((m) => m.id === message.id) ? prev : [...prev, message]));
      setDraft('');
      refreshList();
    } catch (err) {
      const msg = String((err as any)?.message || err);
      setSendError(
        msg.includes('body_too_long')
          ? 'Message trop long (2000 caractères max).'
          : 'Envoi impossible. Réessayez.',
      );
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const openProfile = (c: SupportConversation) => {
    if (!c.otherProfileId) return;
    if (c.otherRole === 'employer') navigate(`/employers/${c.otherProfileId}`);
    else navigate(`/candidates/${c.otherProfileId}`);
  };

  return (
    <div className="h-[calc(100vh-7rem)] flex flex-col gap-4">
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4 flex-shrink-0">
        <div className="w-12 h-12 rounded-xl bg-[#00244F]/10 flex items-center justify-center text-2xl">💬</div>
        <div>
          <p className="text-2xl font-bold text-[#00244F]">{unreadTotal}</p>
          <p className="text-sm text-gray-500">messages non lus</p>
        </div>
        <div className="ml-auto text-right">
          <p className="text-sm font-semibold text-gray-700">{conversations.length} conversations</p>
          <p className="text-xs text-gray-400">Réponses envoyées au nom de PowerJobs</p>
        </div>
      </div>

      <div className="flex-1 min-h-0 bg-white rounded-xl border border-gray-200 flex overflow-hidden">
        {/* Liste */}
        <div className="w-[340px] flex-shrink-0 border-r border-gray-200 flex flex-col min-h-0">
          <div className="p-3 border-b border-gray-100">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un nom, un email…"
              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20 focus:border-[#00244F]"
            />
          </div>
          <div className="flex-1 overflow-y-auto">
            {loadingList ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-7 h-7 border-2 border-[#00244F]/20 border-t-[#00244F] rounded-full animate-spin" />
              </div>
            ) : listError ? (
              <p className="p-4 text-sm text-red-600">{listError}</p>
            ) : filtered.length === 0 ? (
              <p className="p-6 text-sm text-gray-400 text-center">Aucune conversation</p>
            ) : (
              filtered.map((c) => {
                const isActive = c.id === activeId;
                const unread = c.unreadCount > 0;
                return (
                  <button
                    key={c.id}
                    onClick={() => setActiveId(c.id)}
                    className={`w-full text-left px-3 py-3 flex items-center gap-3 border-b border-gray-50 transition-colors ${
                      isActive ? 'bg-[#00244F]/5' : 'hover:bg-gray-50'
                    }`}
                  >
                    <Avatar name={c.other.displayName} uri={c.other.photoUrl} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-sm truncate ${unread ? 'font-bold text-gray-900' : 'font-medium text-gray-800'}`}>
                          {c.other.displayName}
                        </span>
                        <span className="text-[11px] text-gray-400 flex-shrink-0">{formatListTime(c.lastMessageAt)}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border flex-shrink-0 ${roleClasses[c.otherRole] || roleClasses.visitor}`}>
                          {roleLabels[c.otherRole] || c.otherRole}
                        </span>
                        <span className={`text-xs truncate flex-1 ${unread ? 'text-gray-800 font-semibold' : 'text-gray-500'}`}>
                          {c.lastSenderId === POWERJOBS_USER_ID ? 'Vous : ' : ''}
                          {c.lastMessagePreview || ' '}
                        </span>
                        {unread && (
                          <span className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center flex-shrink-0">
                            {c.unreadCount > 99 ? '99+' : c.unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Fil */}
        <div className="flex-1 flex flex-col min-h-0 min-w-0">
          {!active ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
              <span className="text-5xl mb-3">💬</span>
              <p className="text-gray-700 font-medium">Boîte support PowerJobs</p>
              <p className="text-sm text-gray-400 mt-1">
                Sélectionnez une conversation pour lire et répondre au nom de PowerJobs.
              </p>
            </div>
          ) : (
            <>
              <div className="px-5 py-3 border-b border-gray-200 flex items-center gap-3">
                <Avatar name={active.other.displayName} uri={active.other.photoUrl} size="lg" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-gray-900 truncate">{active.other.displayName}</p>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${roleClasses[active.otherRole] || roleClasses.visitor}`}>
                      {roleLabels[active.otherRole] || active.otherRole}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 truncate">
                    {active.otherEmail || '—'}
                    {active.other.subtitle ? ` · ${active.other.subtitle}` : ''}
                  </p>
                </div>
                {active.otherProfileId && (active.otherRole === 'candidate' || active.otherRole === 'employer') && (
                  <button
                    onClick={() => openProfile(active)}
                    className="px-3 py-1.5 text-xs font-medium text-[#00244F] bg-[#00244F]/5 hover:bg-[#00244F]/10 rounded-lg transition-colors"
                  >
                    Voir la fiche →
                  </button>
                )}
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-4 bg-gray-50/60">
                {loadingThread ? (
                  <div className="flex items-center justify-center py-16">
                    <div className="w-7 h-7 border-2 border-[#00244F]/20 border-t-[#00244F] rounded-full animate-spin" />
                  </div>
                ) : messages.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-16">Aucun message</p>
                ) : (
                  <div className="space-y-2">
                    {messages.map((m) => {
                      const mine = m.senderId === POWERJOBS_USER_ID;
                      return (
                        <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                          <div
                            className={`max-w-[70%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap break-words ${
                              mine ? 'bg-[#00244F] text-white rounded-br-md' : 'bg-white border border-gray-200 text-gray-800 rounded-bl-md'
                            }`}
                          >
                            <p>{m.body}</p>
                            <p className={`text-[10px] mt-1 ${mine ? 'text-white/60' : 'text-gray-400'}`}>
                              {mine ? 'PowerJobs · ' : ''}
                              {formatMessageTime(m.createdAt)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={bottomRef} />
                  </div>
                )}
              </div>

              <div className="border-t border-gray-200 p-3">
                {sendError && <p className="text-xs text-red-600 mb-2">{sendError}</p>}
                <div className="flex items-end gap-2">
                  <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Répondre au nom de PowerJobs… (Entrée pour envoyer, Maj+Entrée pour un saut de ligne)"
                    rows={2}
                    maxLength={2000}
                    className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#00244F]/20 focus:border-[#00244F]"
                  />
                  <button
                    onClick={handleSend}
                    disabled={sending || !draft.trim()}
                    className="px-4 py-2.5 text-sm font-medium text-white bg-[#00244F] hover:bg-[#003366] disabled:opacity-40 disabled:cursor-not-allowed rounded-lg transition-colors"
                  >
                    {sending ? '…' : 'Envoyer'}
                  </button>
                </div>
                <p className="text-[10px] text-gray-400 mt-1 text-right">{draft.length}/2000</p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Support;
